import PropTypes from 'prop-types';
import classNames from 'classnames';
import React, { forwardRef } from 'react';
import Spinner from '@scalero-inc/forno-spinner';
import { Link } from 'react-router-dom';
import { jsx, jsxs } from 'react/jsx-runtime';

function _defineProperty(obj, key, value) {
  if (key in obj) {
    Object.defineProperty(obj, key, {
      value: value,
      enumerable: true,
      configurable: true,
      writable: true
    });
  } else {
    obj[key] = value;
  }

  return obj;
}

function ownKeys(object, enumerableOnly) {
  var keys = Object.keys(object);

  if (Object.getOwnPropertySymbols) {
    var symbols = Object.getOwnPropertySymbols(object);
    enumerableOnly && (symbols = symbols.filter(function (sym) {
      return Object.getOwnPropertyDescriptor(object, sym).enumerable;
    })), keys.push.apply(keys, symbols);
  }

  return keys;
}

function _objectSpread2(target) {
  for (var i = 1; i < arguments.length; i++) {
    var source = null != arguments[i] ? arguments[i] : {};
    i % 2 ? ownKeys(Object(source), !0).forEach(function (key) {
      _defineProperty(target, key, source[key]);
    }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys(Object(source)).forEach(function (key) {
      Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key));
    });
  }

  return target;
}

function _objectWithoutPropertiesLoose(source, excluded) {
  if (source == null) return {};
  var target = {};
  var sourceKeys = Object.keys(source);
  var key, i;

  for (i = 0; i < sourceKeys.length; i++) {
    key = sourceKeys[i];
    if (excluded.indexOf(key) >= 0) continue;
    target[key] = source[key];
  }

  return target;
}

function _objectWithoutProperties(source, excluded) {
  if (source == null) return {};
  var target = _objectWithoutPropertiesLoose(source, excluded);
  var key, i;

  if (Object.getOwnPropertySymbols) {
    var sourceSymbolKeys = Object.getOwnPropertySymbols(source);

    for (i = 0; i < sourceSymbolKeys.length; i++) {
      key = sourceSymbolKeys[i];
      if (excluded.indexOf(key) >= 0) continue;
      if (!Object.prototype.propertyIsEnumerable.call(source, key)) continue;
      target[key] = source[key];
    }
  }

  return target;
}

var hierarchies = ['primary', 'secondary', 'tertiary'];
var statuses = ['normal', 'danger'];
var themes = ['light', 'dark'];
var sizesWithLabel = [{
  label: 'extra small',
  value: 'xs'
}, {
  label: 'small',
  value: 's'
}, {
  label: 'medium',
  value: 'm'
}, {
  label: 'large',
  value: 'l'
}];
var sizes = sizesWithLabel.map(function (_ref) {
  var value = _ref.value;
  return value;
});

var BaseButon = /*#__PURE__*/forwardRef(function (props, ref) {
  return /*#__PURE__*/jsx("button", _objectSpread2(_objectSpread2({
    type: "button"
  }, props), {}, {
    ref: ref
  }));
});
BaseButon.displayName = 'BaseButton';

var _excluded$1 = ["to", "children"];
var ButtonAnchor = /*#__PURE__*/React.forwardRef(function (_ref, ref) {
  var to = _ref.to,
      children = _ref.children,
      rest = _objectWithoutProperties(_ref, _excluded$1);

  return /*#__PURE__*/jsx("a", _objectSpread2(_objectSpread2({
    href: to,
    target: "_blank",
    rel: "noopener noreferrer"
  }, rest), {}, {
    ref: ref,
    children: children
  }));
});
ButtonAnchor.propTypes = {
  to: PropTypes.string,
  children: PropTypes.node
};
ButtonAnchor.defaultProps = {
  to: null,
  children: null
};
ButtonAnchor.displayName = 'ButtonAnchor';

var regex = /^(?:[a-z]+:)?\/\//i;
function getButtonPrimitive(_ref) {
  var href = _ref.href;

  if (href == null) {
    return BaseButon;
  }

  var isExternal = regex.test(href);

  if (isExternal) {
    return ButtonAnchor;
  }

  return Link;
}

var _excluded = ["className", "children", "hierarchy", "size", "theme", "loading", "href", "disabled", "icon", "status"];
var classNamePrefix = 'forno-button';
/**
 * Renders a `<Button/>` component
 * @param {Object} props
 * @param {string} [props.className=] - Custom class name
 * @param {string} [props.hierarchy=primary] - [primary, secondary, tertiary, danger]
 * @param {string} [props.size=m] - [s, m, l]
 * @param {string} [props.look=normal] - [normal, outline, text]
 * @param {string} [props.theme=light] - [light, dark]
 * @param {boolean} [props.loading=] - Show a spinner inside the button
 * @param {string} [props.href=] - Use a `<a href={href}>` tag instead of a `<button>` tag
 * @param {boolean} [props.disabled=false] - Disable the button
 * @param {Object} [props.icon=] - Svg icon to prefix the button
 * @param {Object} [props.status=normal] - [normal, danger]
 */

var Button = /*#__PURE__*/forwardRef(function (_ref, ref) {
  var className = _ref.className,
      children = _ref.children,
      hierarchy = _ref.hierarchy,
      size = _ref.size,
      theme = _ref.theme,
      loading = _ref.loading,
      href = _ref.href,
      disabled = _ref.disabled,
      Icon = _ref.icon,
      status = _ref.status,
      rest = _objectWithoutProperties(_ref, _excluded);

  var typography = "font-block-".concat(size, "-sb");
  var customClassName = classNames(classNamePrefix, className, typography);
  var ButtonPrimitive = getButtonPrimitive({
    href: href
  });
  var spinnerHierarchy = hierarchy === 'primary' || hierarchy === 'danger' ? 'neutral' : hierarchy;
  return /*#__PURE__*/jsxs(ButtonPrimitive, _objectSpread2(_objectSpread2({}, rest), {}, {
    ref: ref,
    to: href,
    disabled: disabled,
    "data-size": size,
    "data-hierarchy": hierarchy,
    "data-state": disabled ? 'disabled' : null,
    "data-theme": theme,
    className: customClassName,
    "data-status": status,
    children: [Icon && !loading ? /*#__PURE__*/jsx(Icon, {
      className: "".concat(classNamePrefix, "-icon")
    }) : null, children, loading ? /*#__PURE__*/jsx(Spinner, {
      className: "".concat(classNamePrefix, "-loading"),
      hierarchy: spinnerHierarchy
    }) : null]
  }));
});
Button.displayName = 'Button';
Button.propTypes = {
  className: PropTypes.string,
  children: PropTypes.node,
  hierarchy: PropTypes.oneOf(hierarchies),
  size: PropTypes.oneOf(sizes),
  theme: PropTypes.oneOf(themes),
  icon: PropTypes.func,
  loading: PropTypes.bool,
  href: PropTypes.string,
  disabled: PropTypes.bool,
  status: PropTypes.oneOf(statuses)
};
Button.defaultProps = {
  className: null,
  children: null,
  hierarchy: 'primary',
  size: 'm',
  theme: 'light',
  icon: null,
  loading: false,
  href: null,
  disabled: false,
  status: 'normal'
};

export { Button as default };
